import { User } from '@supabase/supabase-js'
import { useEffect, useState, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import {
  projectRefFromEnv,
  readPersistedAuthUser,
  recoverSessionFromStorage,
} from '../lib/authSessionPersistence'
import { ROUTES } from '../lib/constants'
import { enforceStagingSessionAccess } from '../lib/stagingAccess'
import { devLog, devWarn } from '../lib/devLog'
import {
  captureAuthUnauthorizedFailure,
  isAuthUnauthorizedError,
  isVisibilityWakeRecoveryActive,
} from '../lib/authSentryReporting'
import { identifyPostHogUser, resetPostHogUser } from '../lib/posthog'
import { setUser as setSentryUser } from '../lib/sentry'
import { isTransientNetworkError } from '../lib/transientNetworkError'
import { authBootstrapPromise, supabase } from '../lib/supabase'


interface RequireAuthProps {
  children: React.ReactNode
}

// For development/debugging: always show onboarding
// Set to false in production to only show for new users
const ALWAYS_SHOW_ONBOARDING = false

function RequireAuth({ children }: RequireAuthProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const checkAuthInProgress = useRef(false)
  const hasCheckedAuthOnce = useRef(false)
  const previousUserRef = useRef<User | null>(null)
  const onboardingCacheRef = useRef<{ userId: string; completed: boolean } | null>(null)
  const onboardingAuthBlockedRef = useRef(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Check if we're on onboarding or auth pages - skip loading state for these
  // Use useMemo to prevent recalculation on every render
  const isOnOnboardingPage = useMemo(() => 
    location.pathname.startsWith('/onboarding') || location.pathname === '/onboarding',
    [location.pathname]
  )
  const isOnAuthPage = useMemo(() => 
    location.pathname === '/login'
    || location.pathname === '/auth/callback'
    || location.pathname === '/auth/confirm'
    || location.pathname === '/auth/signup-success',
    [location.pathname]
  )

  // Helper function to get user with timeout
  const getUserWithTimeout = async (timeoutMs: number = 10000): Promise<User | null> => {
    try {
      const getUserPromise = supabase.auth.getUser()
      const getUserTimeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('getUser timeout')), timeoutMs)
      )
      
      const result = await Promise.race([
        getUserPromise,
        getUserTimeoutPromise,
      ]) as { data: { user: User | null } | null; error: { message: string; status?: number } | null }
      
      const { data, error: getUserError } = result
      const user = data?.user ?? null
      
      if (!getUserError && user) {
        devLog('[RequireAuth] getUser() succeeded:', user.id)
        return user
      }

      if (getUserError?.status === 403 || getUserError?.message?.includes('403')) {
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
        if (!refreshError && refreshData.session?.user) {
          devLog('[RequireAuth] refreshSession() recovered user after 403:', refreshData.session.user.id)
          return refreshData.session.user
        }
      }
      return null
    } catch (err) {
      if (isTransientNetworkError(err)) {
        devWarn('[RequireAuth] getUser() transient network error (using fallback)')
        const recovered = await recoverSessionFromStorage(supabase)
        if (recovered) {
          return recovered
        }
        return getUserFromLocalStorage()
      }
      if (err instanceof Error && err.message.includes('timeout')) {
        devLog('[RequireAuth] getUser() timeout (using fallback)')
      }
      return null
    }
  }

  // Helper function to get session with timeout
  const getSessionWithTimeout = async (timeoutMs: number = 5000): Promise<User | null> => {
    try {
      const sessionPromise = supabase.auth.getSession()
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('getSession timeout')), timeoutMs)
      )
      
      const sessionResult = await Promise.race([
        sessionPromise,
        timeoutPromise,
      ]) as { data: { session: { user: User } | null } | null; error: { message: string } | null }
      
      const { data: sessionData } = sessionResult
      const session = sessionData?.session ?? null
      
      devLog('[RequireAuth] getSession() completed, session:', {
        hasSession: !!session,
        hasUser: !!session?.user,
      })
      
      return session?.user ?? null
    } catch (err) {
      if (isTransientNetworkError(err)) {
        devWarn('[RequireAuth] getSession() transient network error (using fallback)')
        return getUserFromLocalStorage()
      }
      if (err instanceof Error && err.message.includes('timeout')) {
        devLog('[RequireAuth] getSession() timeout (using fallback)')
      }
      return null
    }
  }

  const getUserFromLocalStorage = (): User | null => {
    const user = readPersistedAuthUser(projectRefFromEnv())
    if (user) {
      devLog('[RequireAuth] Found persisted auth user in localStorage:', user.id)
    }
    return user
  }

  // Helper function to check onboarding status (cached per user per tab session)
  const loadOnboardingCompleted = async (userId: string): Promise<boolean | null> => {
    if (onboardingAuthBlockedRef.current) {
      devLog('[RequireAuth] Skipping onboarding fetch — auth recovery in progress')
      return null
    }

    const cached = onboardingCacheRef.current
    if (cached?.userId === userId) {
      return cached.completed
    }

    try {
      const onboardingPromise = supabase
        .from('user_onboarding')
        .select('completed_at')
        .eq('user_id', userId)
        .maybeSingle()

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Onboarding check timeout')), 3000),
      )

      const result = (await Promise.race([onboardingPromise, timeoutPromise])) as {
        data: { completed_at?: string } | null
        error: { message: string; status?: number; code?: string } | null
      }

      const { data: onboardingData, error: onboardingError } = result

      devLog('[RequireAuth] Onboarding check result:', {
        hasData: !!onboardingData,
        completed_at: onboardingData?.completed_at,
        error: onboardingError,
      })

      if (onboardingError) {
        if (isAuthUnauthorizedError(onboardingError)) {
          onboardingAuthBlockedRef.current = true
          if (isVisibilityWakeRecoveryActive()) {
            captureAuthUnauthorizedFailure(
              'visibility_wake',
              onboardingError.status ?? 401,
              onboardingError.message,
              'user_onboarding',
            )
          }
          devWarn('[RequireAuth] Onboarding check unauthorized — waiting for session refresh')
          return null
        }
        devWarn('[RequireAuth] Error checking onboarding:', onboardingError)
        return null
      }

      const completed = Boolean(onboardingData?.completed_at)
      onboardingCacheRef.current = { userId, completed }
      onboardingAuthBlockedRef.current = false
      return completed
    } catch (err) {
      if (err instanceof Error && err.message.includes('timeout')) {
        devLog('[RequireAuth] Onboarding check timeout (non-critical, allowing access)')
      } else {
        devWarn('[RequireAuth] Onboarding check failed (non-critical):', err)
      }
      return null
    }
  }

  const applyOnboardingAccess = (completed: boolean | null) => {
    if (!mountedRef.current) return
    if (isOnOnboardingPage || isOnAuthPage || ALWAYS_SHOW_ONBOARDING) {
      setShowOnboarding(false)
      return
    }
    if (completed === null) {
      setShowOnboarding(false)
      return
    }
    setShowOnboarding(!completed)
    if (!completed) {
      navigate(ROUTES.ONBOARDING_STEP_1, { replace: true })
    }
  }

  const checkOnboardingStatus = async (userId: string) => {
    const completed = await loadOnboardingCompleted(userId)
    if (completed === null) {
      return false
    }
    return !completed
  }

  // Helper function to handle authenticated user
  const handleAuthenticatedUser = (currentUser: User) => {
    setSentryUser(currentUser.id, currentUser.email)
    identifyPostHogUser(currentUser.id, {
      plan: 'free',
      signup_source: currentUser.app_metadata?.provider ?? 'email',
    })
    
    if (isOnOnboardingPage || isOnAuthPage) {
      setShowOnboarding(false)
      devLog('[RequireAuth] On onboarding/auth page, skipping onboarding check')
      return
    }
    
    if (ALWAYS_SHOW_ONBOARDING) {
      setShowOnboarding(false)
      devLog('[RequireAuth] ALWAYS_SHOW_ONBOARDING is true, skipping onboarding check')
      return
    }
    
    // Check onboarding in background
    devLog('[RequireAuth] Checking onboarding status for user (non-blocking):', currentUser.id)
    setTimeout(() => {
      checkOnboardingStatus(currentUser.id)
        .then((needsOnboarding) => {
          if (!mountedRef.current) return
          if (needsOnboarding) {
            setShowOnboarding(true)
            navigate(ROUTES.ONBOARDING_STEP_1, { replace: true })
          } else {
            setShowOnboarding(false)
          }
        })
        .catch((err) => {
          if (!mountedRef.current) return
          // Don't log as error - this is non-critical background check
          if (err instanceof Error && err.message.includes('timeout')) {
            devLog('[RequireAuth] Background onboarding check timeout (non-critical)')
          } else {
            devWarn('[RequireAuth] Background onboarding check failed (non-critical):', err)
          }
          setShowOnboarding(false)
        })
    }, 100)
    
    setShowOnboarding(false)
  }

  // Helper function to handle unauthenticated user
  const handleUnauthenticatedUser = () => {
    setSentryUser('', undefined)
    resetPostHogUser()
    setShowOnboarding(false)
    if (!isOnAuthPage) {
      devLog('[RequireAuth] No session, redirecting to login')
      navigate('/login', { replace: true })
    }
  }

  // Helper function to handle auth error
  const handleAuthError = async (err: unknown, _pathname: string): Promise<User | null> => {
    const errorMessage = err instanceof Error ? err.message : String(err)
    if (!isTransientNetworkError(err)) {
      console.error('[RequireAuth] Error in checkAuth:', errorMessage)
    } else {
      devWarn('[RequireAuth] Transient network during auth check:', errorMessage)
    }
    
    if (errorMessage.includes('timeout') || errorMessage.includes('getSession')) {
      devWarn('[RequireAuth] getSession timeout or error, trying getUser()...')
      try {
        const user = await getUserWithTimeout()
        if (user) {
          devLog('[RequireAuth] getUser() succeeded, user:', user.id)
          return user
        }
        const recovered = await recoverSessionFromStorage(supabase)
        if (recovered) {
          devLog('[RequireAuth] recoverSessionFromStorage() succeeded after getUser miss')
          return recovered
        }
        devLog('[RequireAuth] getUser() returned no user, redirecting to login')
        navigate('/login', { replace: true })
        return null
      } catch (getUserErr: unknown) {
        const getUserErrMessage = getUserErr instanceof Error ? getUserErr.message : String(getUserErr)
        if (getUserErrMessage.includes('timeout')) {
          devLog('[RequireAuth] getUser() timeout, but allowing access (user just logged in)')
          return null
        }
        const recovered = await recoverSessionFromStorage(supabase)
        if (recovered) {
          return recovered
        }
        navigate('/login', { replace: true })
        return null
      }
    }

    const recovered = await recoverSessionFromStorage(supabase)
    if (recovered) {
      return recovered
    }

    navigate('/login', { replace: true })
    return null
  }

  useEffect(() => {
    // Prevent multiple simultaneous calls
    if (checkAuthInProgress.current) {
      devLog('[RequireAuth] checkAuth already in progress, skipping')
      return
    }
    
    const checkAuth = async () => {
      checkAuthInProgress.current = true
      try {
        await authBootstrapPromise
        devLog('[RequireAuth] Checking auth, pathname:', location.pathname)
        
        let currentUser: User | null = null
        
        try {
          currentUser = await getSessionWithTimeout()
          if (!currentUser) {
            devWarn('[RequireAuth] getSession() empty, trying getUser()...')
            currentUser = await getUserWithTimeout()
          }
          if (!currentUser) {
            devWarn('[RequireAuth] Auth APIs empty, trying recoverSessionFromStorage()...')
            currentUser = await recoverSessionFromStorage(supabase)
          }
        } catch (err: unknown) {
          // Timeout is expected in some cases, use fallback
          const errorMessage = err instanceof Error ? err.message : String(err)
          if (errorMessage.includes('timeout')) {
            devLog('[RequireAuth] Auth check timeout, using localStorage fallback')
          } else {
            devWarn('[RequireAuth] Auth check failed, using localStorage fallback:', errorMessage)
          }
          currentUser = getUserFromLocalStorage()
          if (!currentUser) {
            currentUser = await recoverSessionFromStorage(supabase)
          }

          // If we had a user before and timeout occurred, don't redirect - user is still logged in
          if (!currentUser && previousUserRef.current && hasCheckedAuthOnce.current) {
            devLog('[RequireAuth] Auth check timeout but user was previously authenticated, keeping session')
            // Keep the previous user
            currentUser = previousUserRef.current
            checkAuthInProgress.current = false
            // Don't update user state, just return
            setLoading(false)
            return
          }
          
          if (!currentUser && (location.pathname === '/' || readPersistedAuthUser(projectRefFromEnv()))) {
            devLog('[RequireAuth] Persisted session or home grace, allowing access despite auth check failure')
            setShowOnboarding(false)
            setLoading(false)
            checkAuthInProgress.current = false
            return
          }
          
          if (!currentUser && location.pathname !== '/') {
            const fallbackUser = await handleAuthError(err, location.pathname)
            if (fallbackUser) {
              currentUser = fallbackUser
            } else {
              return
            }
          }
        }
        
        devLog('[RequireAuth] Auth check result:', {
          hasUser: !!currentUser,
          userId: currentUser?.id,
          email: currentUser?.email,
        })

        if (currentUser) {
          const allowed = await enforceStagingSessionAccess(
            () => supabase.auth.getSession(),
            () => supabase.auth.signOut()
          )
          if (allowed !== true) {
            currentUser = null
            navigate(`/login?oauth_error=${encodeURIComponent(allowed)}`, {
              replace: true,
            })
          }
        }

        setUser(currentUser)
        previousUserRef.current = currentUser
        if (currentUser) {
          hasCheckedAuthOnce.current = true
        }
        
        if (currentUser) {
          handleAuthenticatedUser(currentUser)
        } else {
          handleUnauthenticatedUser()
        }
      } catch (err: unknown) {
        await handleAuthError(err, location.pathname)
      } finally {
        devLog('[RequireAuth] Setting loading to false')
        setLoading(false)
        checkAuthInProgress.current = false
      }
    }

  // Helper function to check onboarding for signed in user
  const checkOnboardingForSignedIn = async (userId: string) => {
    const completed = await loadOnboardingCompleted(userId)
    applyOnboardingAccess(completed)
  }

  // Helper function to check onboarding for other events
  const checkOnboardingForOtherEvent = async (userId: string) => {
    const completed = await loadOnboardingCompleted(userId)
    if (!mountedRef.current) return
    if (completed === null) {
      setShowOnboarding(true)
      return
    }
    setShowOnboarding(!completed)
  }

  // Helper function to handle signed in event
  const handleSignedInEvent = async (userId: string) => {
    if (isOnOnboardingPage || isOnAuthPage) {
      setShowOnboarding(false)
      return
    }
    
    if (ALWAYS_SHOW_ONBOARDING) {
      setShowOnboarding(false)
      return
    }
    
    await checkOnboardingForSignedIn(userId)
  }

  // Helper function to handle other auth events
  const handleOtherAuthEvent = async (userId: string) => {
    if (isOnOnboardingPage || isOnAuthPage) {
      setShowOnboarding(false)
      return
    }
    
    if (ALWAYS_SHOW_ONBOARDING) {
      setShowOnboarding(false)
      return
    }
    
    await checkOnboardingForOtherEvent(userId)
  }

  // Helper function to handle authenticated session
  const handleAuthenticatedSession = async (event: string, userId: string) => {
    if (event === 'TOKEN_REFRESHED') {
      onboardingAuthBlockedRef.current = false
      const cached = onboardingCacheRef.current
      if (cached?.userId === userId) {
        applyOnboardingAccess(cached.completed)
      }
      return
    }
    if (event === 'SIGNED_IN') {
      onboardingAuthBlockedRef.current = false
      await handleSignedInEvent(userId)
    } else {
      await handleOtherAuthEvent(userId)
    }
  }

    // Finish auth init before onAuthStateChange — avoids navigator.locks deadlock (supabase-js #2344)
    let subscription: { unsubscribe: () => void } | null = null
    let cancelled = false

    void (async () => {
      try {
        await supabase.auth.getSession()
      } catch {
        // ignore — checkAuth will handle missing session
      }
      if (cancelled) {
        return
      }

      const { data: { subscription: sub } } = supabase.auth.onAuthStateChange(
        (event: string, session: { user: { id: string; email?: string } } | null) => {
          devLog('[RequireAuth] Auth state changed:', {
            event,
            hasUser: !!session?.user,
            userId: session?.user?.id,
            pathname: location.pathname,
          })

          setUser(session?.user ?? null)
          previousUserRef.current = session?.user ?? null
          if (session?.user) {
            hasCheckedAuthOnce.current = true
          }

          if (session?.user) {
            setSentryUser(session.user.id, session.user.email)
            identifyPostHogUser(session.user.id, {
              plan: 'free',
              signup_source: 'session',
            })
            void handleAuthenticatedSession(event, session.user.id)
          } else {
            setSentryUser('', undefined)
            resetPostHogUser()
            onboardingCacheRef.current = null
            onboardingAuthBlockedRef.current = false
            setShowOnboarding(false)
            if (!isOnAuthPage && event === 'SIGNED_OUT') {
              void recoverSessionFromStorage(supabase).then((recovered) => {
                if (recovered) {
                  setUser(recovered)
                  previousUserRef.current = recovered
                  hasCheckedAuthOnce.current = true
                  handleAuthenticatedUser(recovered)
                  return
                }
                devLog('[RequireAuth] SIGNED_OUT event, redirecting to login')
                navigate('/login', { replace: true })
              })
            } else if (!isOnAuthPage && !session?.user && hasCheckedAuthOnce.current && previousUserRef.current) {
              devLog('[RequireAuth] Session lost but not SIGNED_OUT event, waiting...')
            }
          }
        },
      )
      subscription = sub

      if (!hasCheckedAuthOnce.current) {
        void checkAuth()
      }
    })()

    return () => {
      cancelled = true
      devLog('[RequireAuth] Cleaning up, unsubscribing from auth state changes')
      subscription?.unsubscribe()
    }
  }, [navigate, isOnOnboardingPage, isOnAuthPage])

  // Don't show loading screen if already on onboarding page - let Onboarding handle its own loading
  // This prevents double loading states and flashing
  if (loading && !isOnOnboardingPage && !isOnAuthPage) {
    devLog('[RequireAuth] Showing loading screen')
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    )
  }

  // Reduced logging to improve performance - only log critical issues

  // If no user and not on auth page, don't render anything (redirect is happening)
  // EXCEPT: if we're on home page after login, allow rendering (onAuthStateChange will set user)
  // This prevents flashing of protected content before redirect
  if (!user && !isOnAuthPage) {
    // Special case: if we're on home page and loading is false, 
    // it means we allowed access despite auth check failure (after login)
    // In this case, allow rendering and wait for onAuthStateChange
    if (
      !loading
      && (location.pathname === '/' || readPersistedAuthUser(projectRefFromEnv()))
    ) {
      devLog('[RequireAuth] Grace render while session recovery completes')
      // Allow rendering - onAuthStateChange / recoverSession will set user soon
    } else {
      devLog('[RequireAuth] No user, not on auth page, returning null')
      return null
    }
  }

  // If no user but on auth page, allow rendering (user is already on login/confirm page)
  if (!user && isOnAuthPage) {
    devLog('[RequireAuth] No user, but on auth page, allowing render')
    return <>{children}</>
  }

  // Show onboarding if needed (and not already on onboarding page)
  if (showOnboarding && !isOnOnboardingPage) {
    devLog('[RequireAuth] Onboarding needed, redirecting to onboarding')
    // Redirect to onboarding instead of rendering it here to avoid double rendering
    navigate('/onboarding/step-1', { replace: true })
    return null
  }

  return <>{children}</>
}

export default RequireAuth



